import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

/**
 * Doctor-side alerts for new waiting-room arrivals (#48): chime, toast,
 * flashing tab title until the tab is focused, and a desktop Notification
 * when the doctor granted permission. Which customers count as new is decided
 * by detectNewArrivals (src/lib/notifications/waitingRoomArrivals.ts); this
 * hook only performs the side effects.
 */

const MUTE_KEY = 'namat.waitingRoomAlerts.muted';

type NotificationPermissionState = NotificationPermission | 'unsupported';

function readMuted(): boolean {
  try {
    return window.localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeMuted(muted: boolean) {
  try {
    if (muted) window.localStorage.setItem(MUTE_KEY, '1');
    else window.localStorage.removeItem(MUTE_KEY);
  } catch {
    // storage blocked (private mode etc.) — the toggle still works for this tab
  }
}

function currentPermission(): NotificationPermissionState {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

type AudioContextCtor = typeof AudioContext;

function getAudioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

export interface WaitingRoomArrival {
  customerName?: string;
}

export function useWaitingRoomAlerts({ clinicOpen, language }: { clinicOpen: boolean; language: string }) {
  const [muted, setMutedState] = useState<boolean>(() => readMuted());
  const [permission, setPermission] = useState<NotificationPermissionState>(() => currentPermission());

  const audioCtxRef = useRef<AudioContext | null>(null);
  const flashRef = useRef<{ timer: ReturnType<typeof setInterval>; original: string; count: number } | null>(null);
  // Latest values for the (stable) notify callback used inside Firestore listeners.
  const stateRef = useRef({ clinicOpen, language, muted });
  stateRef.current = { clinicOpen, language, muted };

  const ensureAudio = useCallback((): AudioContext | null => {
    if (!audioCtxRef.current) {
      const Ctor = getAudioContextCtor();
      if (!Ctor) return null;
      try {
        audioCtxRef.current = new Ctor();
      } catch {
        return null;
      }
    }
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    return ctx;
  }, []);

  const playChime = useCallback(() => {
    const ctx = ensureAudio();
    if (!ctx) return;
    try {
      // Two short sine notes (E5 → A5), soft attack and exponential release.
      const notes: Array<[number, number]> = [[659.25, 0], [880, 0.18]];
      for (const [freq, offset] of notes) {
        const start = ctx.currentTime + offset;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, start);
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.25, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.45);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 0.5);
      }
    } catch {
      // audio is best-effort
    }
  }, [ensureAudio]);

  const stopTitleFlash = useCallback(() => {
    const flash = flashRef.current;
    if (!flash) return;
    clearInterval(flash.timer);
    document.title = flash.original;
    flashRef.current = null;
  }, []);

  const startTitleFlash = useCallback((added: number) => {
    const ar = stateRef.current.language === 'ar';
    if (flashRef.current) {
      flashRef.current.count += added;
      return;
    }
    const flash = { original: document.title, count: added, timer: 0 as unknown as ReturnType<typeof setInterval> };
    let showAlert = true;
    const render = () => {
      document.title = showAlert
        ? `(${flash.count}) ${ar ? 'مراجع ينتظر' : 'patient waiting'}`
        : flash.original;
      showAlert = !showAlert;
    };
    render();
    flash.timer = setInterval(render, 1000);
    flashRef.current = flash;
  }, []);

  // Stop flashing as soon as the doctor looks at the tab.
  useEffect(() => {
    const onFocus = () => stopTitleFlash();
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && document.hasFocus()) stopTitleFlash();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      stopTitleFlash();
    };
  }, [stopTitleFlash]);

  useEffect(() => () => {
    audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;
  }, []);

  /** Call with the arrivals from detectNewArrivals; no-op while the clinic is closed. */
  const notifyArrivals = useCallback((arrivals: readonly WaitingRoomArrival[]) => {
    const { clinicOpen: open, language: lang, muted: isMuted } = stateRef.current;
    if (!open || arrivals.length === 0) return;
    const ar = lang === 'ar';
    const names = arrivals.map((a) => a.customerName?.trim() || (ar ? 'مراجع' : 'Patient')).join(ar ? '، ' : ', ');
    const message = ar ? `مراجع جديد في غرفة الانتظار: ${names}` : `New patient in the waiting room: ${names}`;

    if (!isMuted) playChime();
    toast.info(message, { duration: 8000 });

    const unattended = document.visibilityState !== 'visible' || !document.hasFocus();
    if (unattended) startTitleFlash(arrivals.length);

    if (currentPermission() === 'granted' && unattended) {
      try {
        const n = new Notification(ar ? 'غرفة الانتظار' : 'Waiting room', {
          body: message,
          tag: 'namat-waiting-room',
          silent: true, // the chime (or mute) is ours to control
        });
        n.onclick = () => {
          window.focus();
          n.close();
        };
      } catch {
        // some browsers only allow notifications from a service worker
      }
    }
  }, [playChime, startTitleFlash]);

  const setMuted = useCallback((next: boolean) => {
    setMutedState(next);
    writeMuted(next);
    if (!next) ensureAudio(); // user gesture: unlock audio for later chimes
  }, [ensureAudio]);

  const requestPermission = useCallback(async () => {
    ensureAudio(); // the click is a user gesture — unlock audio too
    if (currentPermission() === 'unsupported') return;
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
    } catch {
      setPermission(currentPermission());
    }
  }, [ensureAudio]);

  return { muted, setMuted, permission, requestPermission, notifyArrivals };
}
